import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Download, FileText, Plus, Trash2 } from 'lucide-react';
import {
  formatCents,
  formatDate,
  parseEuroToCents,
  type DepreciationOpenings,
  type TaxLines,
  type TaxSummary,
} from '@vuokramani/shared';
import { DetailList, PageHeader, Section, pageStyles as ps } from '../../components/page.tsx';
import {
  Alert,
  Button,
  buttonClass,
  Dialog,
  DialogActions,
  EmptyState,
  Notice,
  Panel,
  Splash,
  TextField,
} from '../../components/ui.tsx';
import { errorKey } from '../../lib/api.ts';
import {
  useDepreciationOpenings,
  useSaveDepreciationOpenings,
  taxReportUrl,
  useSaveTaxSettings,
  useTaxes,
} from '../../lib/taxes.ts';
import { TAX_LINKS } from '../../lib/taxLinks.ts';
import { YearSwitch } from '../shared/Invoices.tsx';
import { TaxNote } from '../shared/TaxNote.tsx';
import is from '../shared/invoices.module.css';
import s from './taxes.module.css';

type TaxProperty = TaxSummary['properties'][number];

/** Kohdan 2.5 osat näytetään erittelynä, koska poistoja ei näe Talous-sivulta. */
const OTHER_PARTS = [
  'insurance',
  'otherCosts',
  'improvementDepreciation',
  'furnitureImmediate',
  'furnitureDepreciation',
] as const satisfies ReadonlyArray<keyof TaxLines>;

/** Veroilmoitus täytetään keväällä edelliseltä vuodelta: tammi–toukokuussa oletus on viime vuosi. */
function defaultYear() {
  const now = new Date();
  return now.getMonth() < 5 ? now.getFullYear() - 1 : now.getFullYear();
}

/** OmaVeroon kopioitava muoto: "1234,56" (ei tuhaterottimia eikä euromerkkiä). */
function plainEuros(cents: number) {
  return (cents / 100).toFixed(2).replace('.', ',');
}

/**
 * Verot: vuoden luvut asunnoittain lomakkeen 7H kohtien järjestyksessä ja nimillä
 * (OmaVerossa kohta Vuokratulot), omistusosuudella laskettuina.
 */
export function TaxesPage() {
  const { t } = useTranslation();
  const [year, setYear] = useState(defaultYear);
  const { data, isLoading } = useTaxes(year);

  if (isLoading || !data) return <Splash />;

  return (
    <div className={ps.stack}>
      <PageHeader
        title={t('nav.taxes')}
        actions={
          <div className={s.headerActions}>
            {data.properties.length > 0 && (
              <span className={s.downloads}>
                <span className={s.downloadsLabel}>{t('taxes.report')}</span>
                <a href={taxReportUrl(year, 'pdf')} download className={buttonClass('secondary')}>
                  <Download size={16} aria-hidden="true" />
                  PDF
                </a>
                <a href={taxReportUrl(year, 'csv')} download className={buttonClass('secondary')}>
                  <Download size={16} aria-hidden="true" />
                  CSV
                </a>
              </span>
            )}
            <YearSwitch year={year} onChange={setYear} />
          </div>
        }
      />

      {data.properties.length === 0 ? (
        <EmptyState icon={<FileText size={22} />} title={t('taxes.empty', { year })} />
      ) : (
        <>
          <div className={s.intro}>
            <p>{t('taxes.intro', { year })}</p>
            <p>{t('taxes.reportIntro')}</p>
            <Notice>{t('taxes.earlierDepreciation')}</Notice>
          </div>
          {data.properties.map((p) => (
            <PropertyTaxes key={p.propertyId} property={p} year={year} />
          ))}
          <TaxNote />
        </>
      )}
    </div>
  );
}

function PropertyTaxes({ property: p, year }: { property: TaxProperty; year: number }) {
  const { t, i18n } = useTranslation();
  const save = useSaveTaxSettings();
  const [openingsOpen, setOpeningsOpen] = useState(false);
  const money = (c: number) => formatCents(c, i18n.language);
  const date = (d: string) => formatDate(d, i18n.language);
  const l = p.lines;

  const rows: Array<{ key: string; no: string; cents: number }> = [
    { key: 'income', no: '2.1', cents: l.income },
    { key: 'maintenanceAndWater', no: '2.2', cents: l.maintenanceAndWater },
    { key: 'capitalCharges', no: '2.3', cents: l.capitalCharges },
    { key: 'repairs', no: '2.4', cents: l.repairs },
    { key: 'other', no: '2.5', cents: l.other },
  ];
  const parts = OTHER_PARTS.filter((key) => l[key] !== 0);

  return (
    <Section title={p.title} note={p.archived ? t('taxes.archived') : undefined}>
      <Panel>
        <DetailList
          rows={[
            { label: t('taxes.housingCompany'), value: p.housingCompany ?? '–' },
            { label: t('taxes.businessId'), value: p.housingCompanyBusinessId ?? '–' },
            { label: t('taxes.address'), value: p.address },
            {
              label: t('taxes.ownership'),
              value: `${(p.ownershipBp / 100).toLocaleString(i18n.language)} %`,
            },
            {
              label: t('taxes.rentalPeriod'),
              value: p.rentalPeriod
                ? `${date(p.rentalPeriod.start)} – ${date(p.rentalPeriod.end)}`
                : t('taxes.notRented'),
            },
            {
              label: t('taxes.tenant'),
              value:
                p.tenants.length === 0 ? (
                  '–'
                ) : p.tenants.length === 1 ? (
                  p.tenants[0]
                ) : (
                  <>
                    {t('taxes.several')}
                    <span className={s.meta}> ({p.tenants.join(', ')})</span>
                  </>
                ),
            },
          ]}
        />

        {p.type !== 'apartment' && <Alert>{t('taxes.notApartment')}</Alert>}

        <table className={s.lines}>
          <caption className="visually-hidden">{t('taxes.caption')}</caption>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <th scope="row">
                  <span className={s.no}>{row.no}</span>
                  {t(`taxes.line.${row.key}`)}
                  {row.key === 'other' && parts.length > 0 && (
                    <ul className={s.parts}>
                      {parts.map((key) => (
                        <li key={key}>
                          <span>{t(`taxes.part.${key}`)}</span>
                          <span>{money(l[key])}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </th>
                <td>{money(row.cents)}</td>
                <td className={s.copyCell}>
                  <CopyButton cents={row.cents} label={t(`taxes.line.${row.key}`)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {p.outsideRental.length > 0 && (
          <div className={s.warnings}>
            {p.outsideRental.map((e) => (
              <p className={s.warning} key={`${e.paidOn}-${e.description}-${e.amountCents}`}>
                {t(`taxes.timing.${e.timing}`, {
                  date: date(e.paidOn),
                  description: e.description,
                  amount: money(e.amountCents),
                })}{' '}
                <a href={TAX_LINKS.renovations} target="_blank" rel="noreferrer">
                  {t('tax.readMore')}
                </a>
              </p>
            ))}
          </div>
        )}

        <div className={s.openings}>
          <span className={s.meta}>
            {p.hasOpenings ? t('taxes.openingsSet') : t('taxes.openingsNone')}
          </span>
          <button type="button" className={is.linkButton} onClick={() => setOpeningsOpen(true)}>
            {t('taxes.openingsEdit')}
          </button>
          <span className={s.reportLinks}>
            <Download size={14} aria-hidden="true" />
            {t('taxes.propertyReport')}
            <a href={taxReportUrl(year, 'pdf', p.propertyId)} download>
              PDF
            </a>
            ·
            <a href={taxReportUrl(year, 'csv', p.propertyId)} download>
              CSV
            </a>
          </span>
        </div>
        {openingsOpen && (
          <OpeningsDialog
            propertyId={p.propertyId}
            title={p.title}
            onClose={() => setOpeningsOpen(false)}
          />
        )}

        {p.type === 'apartment' && (
          <div className={s.setting}>
            <label className={is.check}>
              <input
                type="checkbox"
                checked={p.capitalChargeIncome}
                disabled={save.isPending}
                onChange={(e) =>
                  save.mutate({
                    propertyId: p.propertyId,
                    body: { capitalChargeIncome: e.target.checked },
                  })
                }
              />
              <span>{t('taxes.capitalChargeIncome')}</span>
            </label>
            <p className={s.meta}>{t('taxes.capitalChargeIncomeHint')}</p>
            {save.isError && <Alert>{t('errors.generic')}</Alert>}
          </div>
        )}

        {l.capitalChargesNotDeducted > 0 && (
          <Notice>
            {t('taxes.capitalNotDeducted', { amount: money(l.capitalChargesNotDeducted) })}
          </Notice>
        )}

        <div className={s.separate}>
          <div className={s.separateRow}>
            <span>{t('taxes.interest')}</span>
            <span className={s.amount}>{money(l.interest)}</span>
            <CopyButton cents={l.interest} label={t('taxes.interest')} />
          </div>
          <p className={s.meta}>{t('taxes.interestHint')}</p>
        </div>

        {(l.improvementRemaining > 0 || l.furnitureRemaining > 0) && (
          <p className={s.meta}>
            {t('taxes.remaining', {
              improvement: money(l.improvementRemaining),
              furniture: money(l.furnitureRemaining),
            })}
          </p>
        )}
      </Panel>
    </Section>
  );
}

/** Kopioi summan leikepöydälle OmaVeroon liitettäväksi. Vanhoissa selaimissa varatapa. */
function CopyButton({ cents, label }: { cents: number; label: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function copy() {
    const text = plainEuros(cents);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const area = document.createElement('textarea');
      area.value = text;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(area);
      }
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button
      type="button"
      className={s.copy}
      onClick={() => void copy()}
      aria-label={t('taxes.copy', { label })}
      title={t('taxes.copy', { label })}
    >
      {copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
      <span className={s.copyText}>{copied ? t('taxes.copied') : t('taxes.copyShort')}</span>
    </button>
  );
}

interface ImprovementRow {
  year: string;
  amount: string;
  description: string;
}

const euroText = (cents: number, lang: string) =>
  (cents / 100).toLocaleString(lang, { minimumFractionDigits: 2 });

/**
 * Ennen Vuokranttia alkaneet poistot: aiemmat perusparannukset (alkuperäinen summa ja
 * maksuvuosi) ja kalusteiden poistamaton hankintameno vuoden alussa.
 */
function OpeningsDialog({
  propertyId,
  title,
  onClose,
}: {
  propertyId: string;
  title: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useDepreciationOpenings(propertyId);
  return (
    <Dialog open onClose={onClose} title={t('taxes.openingsTitle', { title })} wide>
      {isLoading || !data ? (
        <Splash />
      ) : (
        <OpeningsForm propertyId={propertyId} initial={data} onClose={onClose} />
      )}
    </Dialog>
  );
}

function OpeningsForm({
  propertyId,
  initial,
  onClose,
}: {
  propertyId: string;
  initial: DepreciationOpenings;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const save = useSaveDepreciationOpenings(propertyId);
  const thisYear = new Date().getFullYear();
  const [rows, setRows] = useState<ImprovementRow[]>(
    initial.improvements.map((i) => ({
      year: String(i.year),
      amount: euroText(i.amountCents, i18n.language),
      description: i.description,
    })),
  );
  const [balanceYear, setBalanceYear] = useState(
    initial.furnitureBalance ? String(initial.furnitureBalance.year) : String(thisYear),
  );
  const [balance, setBalance] = useState(
    initial.furnitureBalance ? euroText(initial.furnitureBalance.amountCents, i18n.language) : '',
  );
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string>();

  const yearError = (v: string) => {
    const y = Number(v);
    return Number.isInteger(y) && y >= 1990 && y <= thisYear ? undefined : t('errors.year');
  };
  const amountError = (v: string) => {
    const c = parseEuroToCents(v);
    return c !== null && c > 0 ? undefined : t('errors.amount');
  };
  const rowErrors = rows.map((r) => ({ year: yearError(r.year), amount: amountError(r.amount) }));
  const hasBalance = balance.trim() !== '';
  const balanceErrors = hasBalance
    ? { year: yearError(balanceYear), amount: amountError(balance) }
    : { year: undefined, amount: undefined };
  const invalid =
    rowErrors.some((e) => e.year || e.amount) ||
    Boolean(balanceErrors.year || balanceErrors.amount);

  const setRow = (index: number, patch: Partial<ImprovementRow>) =>
    setRows((all) => all.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  async function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    setError(undefined);
    if (invalid) return;
    try {
      await save.mutateAsync({
        improvements: rows.map((r) => ({
          year: Number(r.year),
          amountCents: parseEuroToCents(r.amount)!,
          description: r.description.trim(),
        })),
        furnitureBalance: hasBalance
          ? { year: Number(balanceYear), amountCents: parseEuroToCents(balance)! }
          : null,
      });
      onClose();
    } catch (e) {
      setError(errorKey(e));
    }
  }

  return (
    <form onSubmit={submit} noValidate className={s.openingsForm}>
      <p className={s.meta}>{t('taxes.openingsIntro')}</p>

      <fieldset className={s.fieldset}>
        <legend>{t('taxes.openingsImprovements')}</legend>
        <p className={s.meta}>{t('taxes.openingsImprovementsHint')}</p>
        {rows.map((r, i) => (
          <div key={i} className={s.openingRow}>
            <TextField
              label={t('taxes.openingsYear')}
              inputMode="numeric"
              value={r.year}
              onChange={(e) => setRow(i, { year: e.target.value })}
              error={tried ? rowErrors[i]!.year : undefined}
            />
            <TextField
              label={t('taxes.openingsAmount')}
              inputMode="decimal"
              value={r.amount}
              onChange={(e) => setRow(i, { amount: e.target.value })}
              error={tried ? rowErrors[i]!.amount : undefined}
            />
            <TextField
              label={t('expenses.description')}
              value={r.description}
              maxLength={200}
              onChange={(e) => setRow(i, { description: e.target.value })}
            />
            <Button
              variant="ghost"
              icon
              aria-label={t('common.delete')}
              onClick={() => setRows((all) => all.filter((_, n) => n !== i))}
            >
              <Trash2 size={16} aria-hidden="true" />
            </Button>
          </div>
        ))}
        <div>
          <Button
            variant="secondary"
            onClick={() =>
              setRows((all) => [
                ...all,
                { year: String(thisYear - 1), amount: '', description: '' },
              ])
            }
          >
            <Plus size={16} aria-hidden="true" /> {t('taxes.openingsAddImprovement')}
          </Button>
        </div>
      </fieldset>

      <fieldset className={s.fieldset}>
        <legend>{t('taxes.openingsFurniture')}</legend>
        <p className={s.meta}>{t('taxes.openingsFurnitureHint')}</p>
        <div className={ps.formRow}>
          <TextField
            label={t('taxes.openingsBalanceYear')}
            inputMode="numeric"
            value={balanceYear}
            onChange={(e) => setBalanceYear(e.target.value)}
            error={tried ? balanceErrors.year : undefined}
          />
          <TextField
            label={t('taxes.openingsBalance')}
            hint={t('taxes.openingsBalanceHint')}
            inputMode="decimal"
            value={balance}
            onChange={(e) => setBalance(e.target.value)}
            error={tried ? balanceErrors.amount : undefined}
          />
        </div>
      </fieldset>

      {error && <Alert>{t(error)}</Alert>}
      <DialogActions>
        <Button variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={save.isPending}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </form>
  );
}
